import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UpdateServicesSettingsInput, UpdateStripeSettingsInput, UpdateSmsSettingsInput, UpdateWebrtcSettingsInput, UpdateTwilioVoiceSettingsInput, UpdateTurnstileSettingsInput, UpdatePhoneLookupSettingsInput, UpdateRegistrationCountryInput, UpdateWelcomeBannerInput } from '@klaro7/validators';

import {
  getServicesSettings,
  updateServicesSettings,
  getOpenAIModels,
  getGeminiModels,
  testSmtpConnection,
  testOpenAIChat,
  testOpenAIWhisper,
  testGeminiTranscription,
  getRegistrationStatus,
  getFeatureFlags,
  setFeatureFlag,
  deleteFeatureFlag,
  getConfigStatus,
  testConfig,
  getStripeSettings,
  updateStripeSettings,
  testStripe,
  getSmsSettings,
  updateSmsSettings,
  sendTestSms,
  getWebrtcSettings,
  updateWebrtcSettings,
  testTurn,
  getTurnstileSettings,
  updateTurnstileSettings,
  getPhoneLookupSettings,
  updatePhoneLookupSettings,
  getPhoneLookupStats,
  getRegistrationCountrySettings,
  updateRegistrationCountrySettings,
  getTwilioVoiceSettings,
  updateTwilioVoiceSettings,
  sendTestVoiceCall,
  getWelcomeBanner,
  getWelcomeBannerPublic,
  updateWelcomeBanner,
  uploadWelcomeBannerImage,
  getPublicConfig,
  getSocialSettings,
  updateSocialSettings,
  getGoogleAdsSettings,
  updateGoogleAdsSettings,
  getMetaPixelSettings,
  updateMetaPixelSettings,
} from '../services/settings.service';
import type { SocialSettings, GoogleAdsSettings, MetaPixelSettings } from '../services/settings.service';

export function useServicesSettings() {
  return useQuery({
    queryKey: ['settings', 'services'],
    queryFn: getServicesSettings,
  });
}

export function useUpdateServicesSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateServicesSettingsInput) => updateServicesSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'services'] });
    },
  });
}

export function useTestSmtp() {
  return useMutation({
    mutationFn: testSmtpConnection,
  });
}

export function useTestOpenAIChat() {
  return useMutation({
    mutationFn: testOpenAIChat,
  });
}

export function useTestOpenAIWhisper() {
  return useMutation({
    mutationFn: testOpenAIWhisper,
  });
}

export function useTestGeminiTranscription() {
  return useMutation({
    mutationFn: testGeminiTranscription,
  });
}

export function useGeminiModels(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'gemini', 'models'],
    queryFn: getGeminiModels,
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useOpenAIModels(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'openai', 'models'],
    queryFn: getOpenAIModels,
    enabled,
    staleTime: 5 * 60 * 1000, // cache 5 min
    retry: false,
  });
}

export function useRegistrationStatus() {
  return useQuery({
    queryKey: ['settings', 'registration-status'],
    queryFn: getRegistrationStatus,
    staleTime: 60 * 1000, // cache 1 min
  });
}

export function useFeatureFlags() {
  return useQuery({
    queryKey: ['settings', 'feature-flags'],
    queryFn: getFeatureFlags,
  });
}

export function useSetFeatureFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) => setFeatureFlag(key, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'feature-flags'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'config-status'] });
    },
  });
}

export function useDeleteFeatureFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => deleteFeatureFlag(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'feature-flags'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'config-status'] });
    },
  });
}

export function useConfigStatus(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'config-status'],
    queryFn: getConfigStatus,
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useTestConfig() {
  return useMutation({
    mutationFn: ({ key }: { key: string }) => testConfig(key),
  });
}

// ---- Stripe config (super-admin only) ----

export function useStripeSettings(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'stripe'],
    queryFn: getStripeSettings,
    enabled,
  });
}

export function useUpdateStripeSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateStripeSettingsInput) => updateStripeSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'stripe'] });
    },
  });
}

export function useTestStripe() {
  return useMutation({
    mutationFn: (data: { secretKey?: string } = {}) => testStripe(data),
  });
}

// ---- OVH SMS config (super-admin only) ----

export function useSmsSettings(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'sms'],
    queryFn: getSmsSettings,
    enabled,
  });
}

export function useUpdateSmsSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateSmsSettingsInput) => updateSmsSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'sms'] });
    },
  });
}

export function useSendTestSms() {
  return useMutation({
    mutationFn: (data: { to: string; body?: string }) => sendTestSms(data),
  });
}

// ---- WebRTC / TURN config (super-admin only) ----

export function useWebrtcSettings(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'webrtc'],
    queryFn: getWebrtcSettings,
    enabled,
  });
}

export function useUpdateWebrtcSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateWebrtcSettingsInput) => updateWebrtcSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'webrtc'] });
    },
  });
}

export function useTestTurn() {
  return useMutation({ mutationFn: () => testTurn() });
}

// ---- Cloudflare Turnstile (super-admin only) ----

export function useTurnstileSettings(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'turnstile'],
    queryFn: getTurnstileSettings,
    enabled,
  });
}

export function useUpdateTurnstileSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateTurnstileSettingsInput) => updateTurnstileSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'turnstile'] });
    },
  });
}

// ---- Phone Lookup / VoIP detection (super-admin only) ----

export function usePhoneLookupSettings(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'phone-lookup'],
    queryFn: getPhoneLookupSettings,
    enabled,
  });
}

export function useUpdatePhoneLookupSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdatePhoneLookupSettingsInput) => updatePhoneLookupSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'phone-lookup'] });
    },
  });
}

export function usePhoneLookupStats(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'phone-lookup', 'stats'],
    queryFn: getPhoneLookupStats,
    enabled,
  });
}

// ---- Allow-list paesi in registrazione (super-admin only) ----

export function useRegistrationCountrySettings(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'registration-country'],
    queryFn: getRegistrationCountrySettings,
    enabled,
  });
}

export function useUpdateRegistrationCountrySettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateRegistrationCountryInput) => updateRegistrationCountrySettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'registration-country'] });
    },
  });
}

// ---- Twilio Voice config (super-admin only) ----

export function useTwilioVoiceSettings(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'twilio-voice'],
    queryFn: getTwilioVoiceSettings,
    enabled,
  });
}

export function useUpdateTwilioVoiceSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateTwilioVoiceSettingsInput) => updateTwilioVoiceSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'twilio-voice'] });
    },
  });
}

export function useSendTestVoiceCall() {
  return useMutation({
    mutationFn: (data: { to: string; message?: string }) => sendTestVoiceCall(data),
  });
}

// ---- Welcome banner ----

export function useWelcomeBannerSettings(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'welcome-banner'],
    queryFn: getWelcomeBanner,
    enabled,
  });
}

/** Public banner config for the visitor-facing pop-up (no auth). */
export function useWelcomeBannerPublic() {
  return useQuery({
    queryKey: ['settings', 'welcome-banner', 'public'],
    queryFn: getWelcomeBannerPublic,
    staleTime: 60 * 1000,
    retry: false,
  });
}

export function useUpdateWelcomeBanner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateWelcomeBannerInput) => updateWelcomeBanner(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'welcome-banner'] });
    },
  });
}

export function useUploadWelcomeBannerImage() {
  return useMutation({
    mutationFn: (file: File) => uploadWelcomeBannerImage(file),
  });
}

// ---- Public config (no auth) ----

/** Config runtime pubblica (include i social visibili in home). Cache lunga. */
export function usePublicConfig() {
  return useQuery({
    queryKey: ['settings', 'public-config'],
    queryFn: getPublicConfig,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

// ---- Social links (super-admin only) ----

export function useSocialSettings(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'social'],
    queryFn: getSocialSettings,
    enabled,
  });
}

export function useUpdateSocialSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SocialSettings) => updateSocialSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'social'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'public-config'] });
    },
  });
}

// ---- Google Ads tag (super-admin only) ----

export function useGoogleAdsSettings(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'google-ads'],
    queryFn: getGoogleAdsSettings,
    enabled,
  });
}

export function useUpdateGoogleAdsSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GoogleAdsSettings) => updateGoogleAdsSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'google-ads'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'public-config'] });
    },
  });
}

// ---- Meta Pixel (super-admin only) ----

export function useMetaPixelSettings(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'meta-pixel'],
    queryFn: getMetaPixelSettings,
    enabled,
  });
}

export function useUpdateMetaPixelSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MetaPixelSettings) => updateMetaPixelSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'meta-pixel'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'public-config'] });
    },
  });
}
